import { useState, useEffect, useRef, useCallback } from 'react';
import { getWebsiteContent, updateWebsiteContent } from '../lib/database';
import type { Database } from '../lib/supabase';

type WebsiteContent = Database['public']['Tables']['website_content']['Row'];
type WebsiteContentUpdate = Database['public']['Tables']['website_content']['Update'];

export const useWebsiteContent = () => {
  const [content, setContent] = useState<Record<string, WebsiteContent>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const loadingRef = useRef<boolean>(false);
  const loadedRef = useRef<boolean>(false);

  // Load all website content. Only the first load shows the loading state: later reloads (the
  // public site refreshes every minute and on window focus) swap the content in place, so the page
  // is not unmounted and the reader's scroll position is kept.
  const loadContent = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (loadingRef.current) {
      return Promise.resolve();
    }

    loadingRef.current = true;
    const background = loadedRef.current;
    try {
      if (!background) setLoading(true);
      setError(null);
      const data = await getWebsiteContent();
      const contentMap: Record<string, WebsiteContent> = {};
      
      if (Array.isArray(data)) {
        data.forEach((item) => {
          contentMap[item.section] = item;
        });
      } else if (data) {
        contentMap[(data as WebsiteContent).section] = data as WebsiteContent;
      }
      
      setContent(contentMap);
      loadedRef.current = true;
    } catch (err: any) {
      console.error('Error loading website content:', err);
      // A failed background refresh keeps the content already on screen.
      if (!background) setError(err.message || 'Failed to load website content');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Update content for a specific section
  const updateSection = async (section: string, updates: WebsiteContentUpdate) => {
    try {
      setSaving(true);
      setError(null);
      const updated = await updateWebsiteContent(section, updates);
      
      // Update local state immediately for real-time preview
      setContent((prev) => ({
        ...prev,
        [section]: updated,
      }));
      
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to update website content');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Get content for a specific section
  const getSection = (section: string): WebsiteContent | null => {
    return content[section] || null;
  };

  return {
    content,
    loading,
    error,
    saving,
    updateSection,
    getSection,
    reloadContent: loadContent,
  };
};

